import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { settingsApi } from '../../services/api';
import DispoTimeline from './DispoTimeline';
import DispoInputs from './DispoInputs';
import DispoSimulationTable from './DispoSimulationTable';
import {
  Rhythmus,
  DispoLocalData,
  loadFromLocalStorage,
  saveToLocalStorage,
  getDefaultData,
} from './dispoUtils';
import './DispoContent.css';

const DispoContent: React.FC = () => {
  const { user } = useAuth();

  // Rhythmus aus Settings (Organization oder Global)
  const [rhythmus, setRhythmus] = useState<Rhythmus>('3er');
  const [zeitfenster, setZeitfenster] = useState<number>(96);
  const [settingsLoading, setSettingsLoading] = useState(true);

  // Simulationsdaten
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [bestellmenge, setBestellmenge] = useState(100);
  const [wochenAV, setWochenAV] = useState(60);
  const [bestand, setBestand] = useState(50);
  const [dispoBestand, setDispoBestand] = useState(50);

  // Lade Settings von Organization oder Global
  useEffect(() => {
    const loadSettings = async () => {
      setSettingsLoading(true);
      try {
        // Versuche zuerst aus Organization-Settings (wenn User eingeloggt)
        if (user?.organization?.settings) {
          const orgSettings = user.organization.settings as Record<string, unknown>;
          if (orgSettings.Rythmus) {
            const rythmSetting = orgSettings.Rythmus as { value?: string };
            if (rythmSetting.value === '2er' || rythmSetting.value === '3er') {
              setRhythmus(rythmSetting.value);
            }
          }
        }

        // Lade auch globale Settings für Zeitfenster
        const globalResponse = await settingsApi.getGlobalWithMeta();
        if (globalResponse.settings) {
          const zeitfensterSetting = globalResponse.settings.find(
            (s: { key: string }) => s.key === 'zeitfenster'
          );
          if (zeitfensterSetting?.value) {
            setZeitfenster(Number(zeitfensterSetting.value));
          }

          // Falls kein Org-Rhythmus, nutze Global
          if (!user?.organization?.settings?.Rythmus) {
            const rhythmusSetting = globalResponse.settings.find(
              (s: { key: string }) => s.key === 'Rythmus'
            );
            if (rhythmusSetting?.value === '2er' || rhythmusSetting?.value === '3er') {
              setRhythmus(rhythmusSetting.value);
            }
          }
        }
      } catch (error) {
        console.error('Fehler beim Laden der Settings:', error);
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();
  }, [user]);

  // Lade gespeicherte Daten aus LocalStorage
  useEffect(() => {
    const savedData = loadFromLocalStorage();
    if (savedData) {
      setBestellmenge(savedData.bestellmenge);
      setWochenAV(savedData.wochenAV);
      setBestand(savedData.bestand);
      setDispoBestand(savedData.dispoBestand);
      if (savedData.selectedDate) {
        setSelectedDate(new Date(savedData.selectedDate));
      }
    } else {
      const defaults = getDefaultData();
      setBestellmenge(defaults.bestellmenge);
      setWochenAV(defaults.wochenAV);
      setBestand(defaults.bestand);
      setDispoBestand(defaults.dispoBestand);
    }
  }, []);

  // Speichere Änderungen in LocalStorage
  const saveData = useCallback(() => {
    const data: DispoLocalData = {
      bestellmenge,
      wochenAV,
      bestand,
      dispoBestand,
      selectedDate: selectedDate.toISOString(),
    };
    saveToLocalStorage(data);
  }, [bestellmenge, wochenAV, bestand, dispoBestand, selectedDate]);

  // Auto-Save bei Änderungen
  useEffect(() => {
    const timeoutId = setTimeout(saveData, 500);
    return () => clearTimeout(timeoutId);
  }, [saveData]);

  if (settingsLoading) {
    return (
      <div className="dispo-content">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Lade Einstellungen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dispo-content">
      {/* Header */}
      <div className="dispo-header">
        <h2>Disposition & Bestandsplanung</h2>
        <p className="dispo-description">
          Simulieren Sie Ihren Bestandsverlauf basierend auf dem konfigurierten
          Bestell-Rhythmus ({rhythmus}) und Zeitfenster ({zeitfenster}h).
        </p>
      </div>

      <div className="dispo-settings-info">
        <span className="setting-badge">
          Rhythmus: <strong>{rhythmus}</strong>
        </span>
        <span className="setting-badge">
          Zeitfenster: <strong>{zeitfenster}h</strong>
        </span>
        {user?.organization && (
          <span className="setting-badge org">
            {user.organization.name}
          </span>
        )}
      </div>

      {/* Timeline */}
      <DispoTimeline
        rhythmus={rhythmus}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        onRhythmusChange={setRhythmus}
      />

      {/* Inputs */}
      <DispoInputs
        bestellmenge={bestellmenge}
        wochenAV={wochenAV}
        bestand={bestand}
        dispoBestand={dispoBestand}
        onBestellmengeChange={setBestellmenge}
        onWochenAVChange={setWochenAV}
        onBestandChange={setBestand}
        onDispoBestandChange={setDispoBestand}
      />

      {/* Simulation Table */}
      <DispoSimulationTable
        startDate={selectedDate}
        bestand={bestand}
        dispoBestand={dispoBestand}
        wochenAV={wochenAV}
        bestellmenge={bestellmenge}
        rhythmus={rhythmus}
      />
    </div>
  );
};

export default DispoContent;
